import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { logger } from '../../utils/logger'
import Footer from '../layout/Footer'
import TopSafeSpacer from '../layout/TopSafeSpacer'
import { productApi, Product } from '../../services/api'
import { useCartContext } from '../../contexts/CartContext'
import { User, Package, Store, MapPin, ShoppingCart } from 'lucide-react'
import { formatPrice } from '../../utils/currency'

export default function PersonalStore() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { addToCart } = useCartContext()
  const tg = window.Telegram?.WebApp

  const [storeData, setStoreData] = useState<{
    user: {
      telegram_id: number
      username?: string
      first_name?: string
      last_name?: string
    }
    products: Product[]
    total: number
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [addingToCart, setAddingToCart] = useState<string | null>(null)

  // Fetch store data
  useEffect(() => {
    const fetchStore = async () => {
      if (!userId) {
        setError('User ID is missing')
        setLoading(false)
        return
      }

      try {
        logger.debug('PersonalStore: Fetching store', userId)
        const data = await productApi.getPersonalStore(userId)
        logger.debug('PersonalStore: Store fetched', data)
        setStoreData(data)
      } catch (error) {
        logger.error('PersonalStore: Error fetching store:', error)
        setError(error instanceof Error ? error.message : 'Failed to load store')
      } finally {
        setLoading(false)
      }
    }

    fetchStore()
  }, [userId])

  // Store the current store URL in sessionStorage for navigation back
  useEffect(() => {
    if (userId && storeData?.user) {
      sessionStorage.setItem('lastVisitedStore', `/store/${userId}`)
      const storeName = storeData.user.first_name || storeData.user.username || 'Store'
      sessionStorage.setItem('storeOwnerName', storeName)
      logger.debug('PersonalStore: Saved store to sessionStorage', { userId, storeName })
    }
  }, [userId, storeData])

  const handleProductClick = (productId: string) => {
    logger.logNavigation(`/store/${userId}`, `/product/${productId}`)
    navigate(`/product/${productId}`)
  }

  const handleAddToCart = async (productId: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent navigation to product detail

    try {
      setAddingToCart(productId)
      await addToCart(productId, 1)

      // Show success feedback
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success')
      }

      if (tg?.showAlert) {
        tg.showAlert('Added to cart!')
      }

      logger.debug('PersonalStore: Added to cart', productId)
    } catch (error) {
      logger.error('PersonalStore: Failed to add to cart:', error)
      if (tg?.showAlert) {
        tg.showAlert('Failed to add to cart')
      }
    } finally {
      setAddingToCart(null)
    }
  }

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        {/* Top padding for Telegram UI */}
        <TopSafeSpacer />

        <main className="flex-1 overflow-auto p-4">
          <div className="max-w-md mx-auto text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-3"></div>
            <p className="text-gray-600 text-sm">Loading store...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error || !storeData) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        {/* Top padding for Telegram UI */}
        <TopSafeSpacer />

        <main className="flex-1 overflow-auto p-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 text-center">
              <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">Store Not Found</h2>
              <p className="text-gray-500 mb-6">{error || 'Could not load store'}</p>
              <button
                onClick={() => navigate('/products')}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
              >
                Browse All Products
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  const { user, products, total } = storeData
  const storeName = user.first_name || user.username || 'Store'

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Top padding for Telegram UI */}
      <TopSafeSpacer />

      {/* Main Content */}
      <main className="flex-1 overflow-auto p-4">
        <div className="max-w-md mx-auto space-y-4">
          {/* Store Owner Info with Store Title */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            {/* Store Title */}
            <div className="mb-4 pb-3 border-b border-gray-200">
              <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Store className="w-5 h-5 text-blue-600" />
                <span>{storeName}'s Store</span>
              </h1>
            </div>

            {/* Owner Info */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-800">
                  {user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user.first_name || user.username || 'Anonymous'}
                </h2>
                {user.username && (
                  <p className="text-sm text-gray-500">@{user.username}</p>
                )}
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{total}</p>
                <p className="text-xs text-gray-500">Products</p>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          {products.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 text-center">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">No Products Yet</h3>
              <p className="text-gray-500">This store doesn't have any products listed yet.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-800">Available Products</h2>
                <span className="text-sm text-gray-500">{products.length} items</span>
              </div>

              <div className="grid gap-3">
                {products.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => handleProductClick(product.id)}
                    className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex">
                      {/* Product Image */}
                      <div className="w-24 h-24 bg-gray-100 flex items-center justify-center flex-shrink-0 relative">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={product.images[0].image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="w-10 h-10 text-gray-400" />
                        )}
                        {product.images && product.images.length > 1 && (
                          <div className="absolute bottom-1 right-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full font-medium">
                            +{product.images.length - 1}
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 p-3">
                        <h3 className="font-medium text-gray-800 dark:text-gray-100 text-sm">{product.name}</h3>

                        {product.city_name && (
                          <div className="flex items-center gap-1 text-gray-500 text-xs mt-0.5 mb-1">
                            <MapPin className="w-3 h-3" />
                            <span>{product.city_name}</span>
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-auto">
                          <div>
                            <span className="text-base font-semibold text-blue-600">
                              {formatPrice(product.price, product.currency)}
                            </span>
                            {product.compare_price && product.compare_price > product.price && (
                              <span className="ml-1.5 text-xs text-gray-400 line-through">
                                {formatPrice(product.compare_price, product.currency)}
                              </span>
                            )}
                          </div>

                          {product.quantity !== undefined && product.quantity > 0 && (
                            <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium border border-green-200">
                              {product.quantity} left
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Add to Cart section */}
                    <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
                      <button
                        onClick={(e) => handleAddToCart(product.id, e)}
                        disabled={addingToCart === product.id}
                        className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {addingToCart === product.id ? (
                          'Adding...'
                        ) : (
                          <>
                            <ShoppingCart className="w-4 h-4" />
                            Add to Cart
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Browse All Products Link */}
          <div className="mt-6 text-center pb-4">
            <button
              onClick={() => navigate('/products')}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
            >
              Browse All Products →
            </button>
          </div>
          <Footer />
        </div>
      </main>
    </div>
  )
}
